import type { ConversationThread, DecryptedNotification } from "../storage-schema";
import type { TriageInput } from "./triage";

export interface ClassifiableItem {
  id: string; // standalone: msgId; thread: conversationKey
  msgId?: string;
  conversationKey?: string;
  appPackage: string;
  app: string;
  repliable: boolean;
  input: TriageInput;
}

/** Pure: in-window, non-hidden items to classify — one per standalone notification, one per thread (latest message). */
export function gatherDigestItems(args: {
  notifications: DecryptedNotification[];
  conversations: ConversationThread[];
  windowStart: number;
  prefs: { hiddenApps: string[] };
}): ClassifiableItem[] {
  const hidden = new Set(args.prefs.hiddenApps);
  const items: ClassifiableItem[] = [];

  // windowStart is an exclusive lower bound — an item at exactly windowStart is out of window.
  for (const n of args.notifications) {
    if (n.timestamp <= args.windowStart || hidden.has(n.appPackage)) continue;
    items.push({
      id: n.msgId,
      msgId: n.msgId,
      appPackage: n.appPackage,
      app: n.app,
      repliable: n.actions?.some((a) => a.actionType === "reply") ?? false,
      input: {
        app: n.app,
        title: n.title,
        body: n.body,
        timestamp: n.timestamp,
        category: n.category,
      },
    });
  }

  for (const t of args.conversations) {
    if (t.lastMessageTimestamp <= args.windowStart || hidden.has(t.packageName)) continue;
    const recent = t.messages.filter((m) => m.timestamp > args.windowStart);
    // A thread can pass the lastMessageTimestamp gate yet have no post-window messages (denormalized field runs ahead) — skip it.
    if (recent.length === 0) continue;
    const last = recent.reduce((a, b) => (a.timestamp >= b.timestamp ? a : b));
    items.push({
      id: t.key,
      conversationKey: t.key,
      appPackage: t.packageName,
      app: t.app,
      repliable: (t.actions ?? []).some((a) => a.actionType === "reply"),
      input: {
        app: t.app,
        title: t.isGroup ? (t.groupName ?? t.title) : t.title,
        body: recent.length > 1 ? `(${recent.length} messages, latest) ${last.text}` : last.text,
        timestamp: last.timestamp,
      },
    });
  }

  return items;
}
